import React from 'react';
import G2FirmwareUpdate from './g2-firmware-update';
import azenaLogo from '../../assets/images/logo_horiz.png';
import heroImage from '../../assets/images/evo-firmware-update-hero.png';
import deviceImage from '../../assets/images/evo-firmware-update-device.png';
import './g2-firmware-update-v2.css';

const DRIVER_GUIDE_URL =
  '/guides/Gemini_EVO_Installation_Guide.pdf';
const WINDOWS_DRIVER_URL =
  'https://g2-support-materials.s3.us-west-2.amazonaws.com/Drivers/CP210x_VCP_Windows.zip';
const CABLE_URL = 'https://www.amazon.com/dp/B00NH13S44';

const UPDATE_STEPS = [
  {
    title: 'Connect the mini-USB cable',
    detail: (
      <>
        <a href={CABLE_URL} target="_blank" rel="noopener noreferrer">
          Buy a cable
        </a>{' '}
        — not included
      </>
    ),
  },
  {
    title: 'Turn the unit on',
    detail: 'Let it boot — no buttons to press',
  },
  {
    title: 'Start the update',
    detail: 'Choose the Silicon Labs UART port',
  },
  {
    title: 'The unit restarts itself',
    detail: 'Nothing more for you to do',
  },
];

class G2FirmwareUpdateV2 extends G2FirmwareUpdate {
  getCurrentStage = () => {
    const {
      connecting,
      connected,
      flashing,
      monitoring,
      progress,
      showDriverHelp,
      success,
      error,
    } = this.state;

    if (success) return 'done';
    if (error) return 'error';
    if (showDriverHelp) return 'driver';
    if (monitoring || progress === 100) return 'restarting';
    if (flashing) return 'installing';
    if (connected) return 'verifying';
    if (connecting) return 'connecting';
    return 'ready';
  };

  getCurrentStep = (stage) => {
    if (stage === 'done') return 5;
    if (stage === 'restarting') return 4;
    if (
      stage === 'connecting' ||
      stage === 'driver' ||
      stage === 'verifying' ||
      stage === 'installing' ||
      stage === 'error'
    ) {
      return 3;
    }
    return 1;
  };

  showDriverInstructions = () => {
    this.setState({ showDriverHelp: true, error: '' });
  };

  retryConnection = () => {
    this.setState({ showDriverHelp: false, error: '' }, this.handleConnect);
  };

  handleUpdateAnother = async () => {
    await this.stopMonitor();
    await this.cleanupTransport();
    this.setState({
      connecting: false,
      connected: false,
      flashing: false,
      monitoring: false,
      showAdvanced: false,
      showDriverHelp: false,
      chipName: '',
      flashStatus: '',
      customerStatus: 'Ready to connect your device.',
      error: '',
      progress: 0,
      selectedPortInfo: '',
      logLines: [],
      firmwareFilesLoaded: false,
      success: false,
      successMessage: '',
    });
  };

  renderBrandMark = () => (
    <img className="evo-update-brand" src={azenaLogo} alt="Azena Medical" width="750" height="230" />
  );

  renderDeviceShot = (linkState = 'off', dim = false) => {
    const labels = {
      off: 'Not connected',
      seeking: 'Looking for the unit',
      connected: 'Connected',
      disconnected: 'Disconnected',
    };

    return (
      <div className={`evo-update-shot${dim ? ' is-dimmed' : ''}`}>
        <img src={deviceImage} alt="Gemini EVO console" />
        <div className={`evo-update-badge is-${linkState}`}>
          <i aria-hidden="true" />
          {labels[linkState]}
        </div>
      </div>
    );
  };

  renderProgress = () => {
    const { progress } = this.state;
    let phase = 'Writing control board';

    if (progress >= 86) phase = 'Verifying update';
    else if (progress >= 64) phase = 'Writing 980 nm driver';
    else if (progress >= 38) phase = 'Writing 810 nm driver';

    const minutesLeft = Math.max(1, Math.round(((100 - progress) * 3) / 60));
    const estimate =
      progress > 92
        ? 'almost done'
        : `about ${minutesLeft} minute${minutesLeft === 1 ? '' : 's'} left`;

    return (
      <div>
        <div className="evo-update-readout">
          <span className="evo-update-percent">{progress}%</span>
          <span className="evo-update-phase">
            <strong>{phase}</strong>
            <span>{estimate}</span>
          </span>
        </div>
        <div
          className="evo-update-progress"
          role="progressbar"
          aria-label="Firmware update progress"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={progress}
        >
          <i style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  };

  renderStage = (stage) => {
    const { chipName, error, progress } = this.state;

    if (stage === 'connecting') {
      return (
        <>
          <div>
            <h3>Pick the Silicon Labs UART port</h3>
            <p className="evo-update-copy">
              Choose <strong>Silicon Labs CP210x USB to UART Bridge</strong> in
              Chrome&apos;s list, then click Connect.
            </p>
          </div>
          {this.renderDeviceShot('seeking')}
          <div className="evo-update-waiting" role="status">
            Waiting for the browser&apos;s device selection…
          </div>
          <p className="evo-update-copy is-small">
            List empty, or no compatible devices found? Cancel the prompt, then{' '}
            <button className="evo-update-link" onClick={this.showDriverInstructions}>
              install the USB driver
            </button>
            .
          </p>
        </>
      );
    }

    if (stage === 'driver') {
      return (
        <>
          <div>
            <p className="evo-update-eyebrow">ONE-TIME SETUP ON THIS COMPUTER</p>
            <h3>The unit isn&apos;t showing up in the list</h3>
            <p className="evo-update-copy">
              Almost always this means Windows doesn&apos;t yet have the driver that
              lets it see the Gemini EVO. Installing it takes about two minutes,
              and only has to be done once per computer.
            </p>
          </div>
          <ol className="evo-update-howto">
            <li>
              <span>1</span>
              <div>
                <strong>Download the driver</strong>
                Save <code>CP210x_VCP_Windows.zip</code> — about 3.7 MB.
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <strong>Unzip it</strong>
                In Downloads, right-click the file, choose <b>Extract All</b>, then{' '}
                <b>Extract</b>.
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <strong>Run the installer</strong>
                Open the extracted folder and double-click{' '}
                <code>CP210xVCPInstaller_x64.exe</code>. If Windows says that is
                the wrong one, use the <code>x86</code> file instead.
              </div>
            </li>
            <li>
              <span>4</span>
              <div>
                <strong>Click through it</strong>
                Next, accept the agreement, Next, then Finish.
              </div>
            </li>
            <li>
              <span>5</span>
              <div>
                <strong>Come back here</strong>
                The unit will now appear as <b>Silicon Labs CP210x USB to UART Bridge</b>.
              </div>
            </li>
          </ol>
          <div className="evo-update-actions">
            <a
              className="evo-update-download"
              href={WINDOWS_DRIVER_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download the driver <small>Windows · 3.7 MB</small>
            </a>
            <button className="evo-update-ghost" onClick={this.retryConnection}>
              Try connecting again
            </button>
          </div>
          <p className="evo-update-copy is-small">
            Need illustrated steps?{' '}
            <a href={DRIVER_GUIDE_URL} target="_blank" rel="noopener noreferrer">
              Open the installation guide
            </a>
            . Still stuck after installing? Call Ultradent Equipment Repair at{' '}
            801-553-4574.
          </p>
        </>
      );
    }

    if (stage === 'verifying') {
      return (
        <>
          <div>
            <h3>Gemini EVO found</h3>
            <p className="evo-update-copy">
              Checking the 2.2 package before anything is written to the unit.
              {chipName ? ` Connected to ${chipName}.` : ''}
            </p>
          </div>
          {this.renderDeviceShot('connected')}
          <div className="evo-update-progress is-verifying">
            <i />
          </div>
        </>
      );
    }

    if (stage === 'installing') {
      return (
        <>
          <div className="evo-update-hazard">
            <span aria-hidden="true">!</span>
            Do not unplug the cable or switch the unit off
          </div>
          {this.renderDeviceShot('connected')}
          {this.renderProgress()}
        </>
      );
    }

    if (stage === 'restarting') {
      return (
        <>
          <div>
            <h3>Update written — the unit is restarting</h3>
            <p className="evo-update-copy">
              This takes about 20 seconds. Leave it powered on until the home screen
              comes back.
            </p>
          </div>
          {this.renderDeviceShot('connected')}
          <div className="evo-update-progress">
            <i style={{ width: `${Math.max(progress, 100)}%` }} />
          </div>
        </>
      );
    }

    if (stage === 'done') {
      return (
        <>
          <div>
            <h3>Gemini EVO is now on 2.2</h3>
            <p className="evo-update-copy">
              You can unplug the cable — the unit is ready to use. The version shows
              on screen each time it starts up, and will now read <strong>2.2</strong>.
            </p>
          </div>
          {this.renderDeviceShot('connected')}
          <div className="evo-update-notes">
            <h4>What&apos;s new in 2.2</h4>
            <dl>
              <div>
                <dt>Easier PBM timing</dt>
                <dd>
                  Long-pressing to add seconds to a PBM procedure now moves in rounded
                  numbers, so it lands where you expect.
                </dd>
              </div>
              <div>
                <dt>Steadier power supply</dt>
                <dd>
                  Power management has been tuned for more consistent output and
                  reliability.
                </dd>
              </div>
              <div>
                <dt>Bug fixes</dt>
                <dd>Small issues have been resolved for smoother day-to-day operation.</dd>
              </div>
              <div>
                <dt>Performance</dt>
                <dd>Improvements across the system make the unit quicker to work with.</dd>
              </div>
            </dl>
          </div>
          <button className="evo-update-ghost" onClick={this.handleUpdateAnother}>
            Update another unit
          </button>
        </>
      );
    }

    if (stage === 'error') {
      return (
        <>
          <div>
            <h3 className="is-error">The update did not complete</h3>
            <p className="evo-update-copy">
              Nothing was damaged. The Gemini EVO keeps its previous software until
              an update finishes. To try again:
            </p>
            <ol className="evo-update-trouble">
              <li>Reseat both ends of the mini-USB cable.</li>
              <li>Use a USB port on the computer itself, not a hub or dock.</li>
              <li>Switch the unit off, wait five seconds, and switch it back on.</li>
            </ol>
          </div>
          {this.renderDeviceShot('disconnected', true)}
          <div className="evo-update-error-detail">{error}</div>
          <button className="evo-update-primary" onClick={this.handleConnect}>
            Try the update again
          </button>
          <p className="evo-update-copy is-small">
            Third failure in a row? Call Ultradent Equipment Repair at 801-553-4574
            and quote error <strong>USB-07</strong>.
          </p>
        </>
      );
    }

    return (
      <>
        <div>
          <h3>Connect the cable, then turn the unit on</h3>
          <p className="evo-update-copy">
            Once it has finished booting, click below. Chrome will ask which device
            to connect — choose the <strong>Silicon Labs UART</strong> port. First
            time on this computer? You may need to{' '}
            <button className="evo-update-link" onClick={this.showDriverInstructions}>
              install the USB driver
            </button>{' '}
            first.
          </p>
        </div>
        {this.renderDeviceShot('off')}
        <button className="evo-update-primary" onClick={this.handleConnect}>
          Connect and update
        </button>
      </>
    );
  };

  renderAdvanced = () => {
    if (!this.state.showAdvanced) return null;

    const { chipName, selectedPortInfo, flashStatus, logLines } = this.state;
    return (
      <section className="evo-update-advanced" aria-label="Technical update details">
        <div className="evo-update-advanced-grid">
          <div>
            <span>Target version</span>
            <strong>2.2</strong>
          </div>
          <div>
            <span>Port</span>
            <strong>{selectedPortInfo || 'Not selected'}</strong>
          </div>
          <div>
            <span>Chip</span>
            <strong>{chipName || 'Not connected'}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{flashStatus || 'Ready'}</strong>
          </div>
        </div>
        <pre>{logLines.length ? logLines.join('\n') : 'No log output yet.'}</pre>
      </section>
    );
  };

  render() {
    const stage = this.getCurrentStage();
    const currentStep = this.getCurrentStep(stage);

    return (
      <div className="evo-updater-v2">
        <div className="evo-update-wrap">
          <header className="evo-update-masthead">
            {this.renderBrandMark()}
            <div className="evo-update-build">
              Update package <strong>EVO 2.2</strong>
              <br />
              Released 14 August 2026
            </div>
          </header>

          <section className="evo-update-hero">
            <img src={heroImage} alt="Gemini EVO 810 + 980 Diode Laser" />
            <div>
              <h1>Update your Gemini EVO</h1>
              <p>
                Four steps, done from this page. You&apos;ll need a mini-USB cable,
                which isn&apos;t supplied with the unit —{' '}
                <a href={CABLE_URL} target="_blank" rel="noopener noreferrer">
                  this one from Amazon
                </a>{' '}
                works.
              </p>
              <p className="evo-update-meta">
                Takes <strong>3 to 5 minutes</strong> · Works in{' '}
                <strong>Google Chrome or Microsoft Edge</strong>
              </p>
            </div>
          </section>

          <div className="evo-update-console">
            <nav className="evo-update-rail" aria-label="Update steps">
              <h2>How this works</h2>
              <ol>
                {UPDATE_STEPS.map((step, index) => {
                  const stepNumber = index + 1;
                  const status =
                    stepNumber < currentStep
                      ? 'is-done'
                      : stepNumber === currentStep
                        ? 'is-current'
                        : '';

                  return (
                    <li className={`evo-update-step ${status}`} key={step.title}>
                      <div className="evo-update-dot">
                        {stepNumber < currentStep ? '✓' : stepNumber}
                      </div>
                      <div>
                        <span>{step.title}</span>
                        <small>{step.detail}</small>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <div className="evo-update-support">
                Ultradent Equipment Repair
                <strong>801-553-4574</strong>
              </div>
              <button
                className="evo-update-technical-toggle"
                onClick={() => this.setState({ showAdvanced: !this.state.showAdvanced })}
                aria-expanded={this.state.showAdvanced}
              >
                {this.state.showAdvanced ? 'Hide technical details' : 'Technical details'}
              </button>
            </nav>

            <main className="evo-update-stage" aria-live="polite">
              {this.renderStage(stage)}
            </main>
          </div>

          {this.renderAdvanced()}
        </div>
      </div>
    );
  }
}

export default G2FirmwareUpdateV2;
